import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { ConditionDescriptor } from "../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField, DimensionDescriptor } from "../../../../../api/types/svTypes.ts";
import type { DrillDownConditionNode } from "./drillDownConditions.ts";
import {
  addTopLevelDrillDownCondition,
  mapConditionDescriptorsToTree,
  removeDrillDownConditionField,
  updateDrillDownConditionField,
} from "./drillDownConditions.ts";
import type { FieldWithOrder, FieldWithSorting } from "./FieldWithOrder";

export type FieldsStateConfiguration = {
  conditionTree: DrillDownConditionNode[];
  selectedFields: FieldWithOrder[];
  sortedFields: FieldWithSorting[];
  measureName: string | undefined;
  error: string | undefined;
};

export const initializeDrilldown = ({
  conditions,
  dimensions,
}: {
  conditions: ConditionDescriptor[];
  dimensions: DimensionDescriptor[];
}): FieldsStateConfiguration => {
  return {
    conditionTree: mapConditionDescriptorsToTree(conditions, dimensions),
    selectedFields: [],
    sortedFields: [],
    measureName: undefined,
    error: undefined,
  };
};

const fieldSateConfigurationInitialState: FieldsStateConfiguration = {
  conditionTree: [],
  selectedFields: [],
  sortedFields: [],
  measureName: undefined,
  error: undefined,
};

const fieldsStateConfigurationSlice = createSlice({
  name: "fieldsStateConfiguration",
  initialState: fieldSateConfigurationInitialState,
  reducers: {
    addCondition(state, action: PayloadAction<ConditionField>) {
      state.conditionTree = addTopLevelDrillDownCondition(state.conditionTree, action.payload);
    },
    updateCondition(state, action: PayloadAction<{ field: ConditionField; changes: Partial<ConditionField> }>) {
      const { payload } = action;
      state.conditionTree = updateDrillDownConditionField(state.conditionTree, payload.field, payload.changes);
    },
    removeCondition(state, action: PayloadAction<ConditionField>) {
      state.conditionTree = removeDrillDownConditionField(state.conditionTree, action.payload);
    },
  },
});

export const fieldStateActions = fieldsStateConfigurationSlice.actions;

export const fieldStateReducer = fieldsStateConfigurationSlice.reducer;
